import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import {
    Brain,
    Volume2,
    VolumeX,
    Trash2,
    FileText,
    Pause,
} from "lucide-react";
import { toast } from "sonner";
import { deleteAnalysis } from "../lib/api";

/**
 * Very small markdown → React renderer. Supports H2/H3, bullets, bold, code —
 * enough for Claude's structured analysis output without pulling a marked lib.
 */
function renderMarkdown(md) {
    if (!md) return null;
    const lines = md.split(/\r?\n/);
    const out = [];
    let inList = false;
    const flushList = () => {
        if (inList) {
            out.push(null); // list close placeholder
            inList = false;
        }
    };
    const nodes = [];
    let listItems = [];
    const pushList = () => {
        if (listItems.length) {
            nodes.push({ type: "ul", items: listItems });
            listItems = [];
        }
    };
    for (const raw of lines) {
        const line = raw.replace(/\s+$/, "");
        if (!line) {
            pushList();
            continue;
        }
        if (line.startsWith("## ")) {
            pushList();
            nodes.push({ type: "h2", text: line.slice(3) });
            continue;
        }
        if (line.startsWith("### ")) {
            pushList();
            nodes.push({ type: "h3", text: line.slice(4) });
            continue;
        }
        const bullet = line.match(/^\s*[-*]\s+(.*)$/);
        if (bullet) {
            listItems.push(bullet[1]);
            continue;
        }
        pushList();
        nodes.push({ type: "p", text: line });
    }
    pushList();

    const inline = (text) => {
        // Bold + inline code
        const parts = [];
        const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
        let last = 0;
        let m;
        let idx = 0;
        while ((m = re.exec(text)) !== null) {
            if (m.index > last) parts.push(text.slice(last, m.index));
            const token = m[0];
            if (token.startsWith("**")) {
                parts.push(
                    <strong key={`b${idx++}`} className="text-white font-bold">
                        {token.slice(2, -2)}
                    </strong>,
                );
            } else if (token.startsWith("`")) {
                parts.push(
                    <code
                        key={`c${idx++}`}
                        className="font-mono bg-[#0A1128] text-[#00A3FF] px-1 rounded-sm text-[0.9em]"
                    >
                        {token.slice(1, -1)}
                    </code>,
                );
            }
            last = m.index + token.length;
        }
        if (last < text.length) parts.push(text.slice(last));
        return parts;
    };

    return nodes.map((n, i) => {
        if (n.type === "h2")
            return (
                <h2 key={i} className="text-lg font-bold text-white mt-4 mb-1.5 flex items-center gap-2">
                    <span className="w-1 h-4 bg-[#00A3FF] rounded-sm" />
                    {inline(n.text)}
                </h2>
            );
        if (n.type === "h3")
            return (
                <h3 key={i} className="text-sm font-bold text-white mt-3 mb-1">
                    {inline(n.text)}
                </h3>
            );
        if (n.type === "ul")
            return (
                <ul key={i} className="list-disc ml-5 my-1.5 space-y-1 text-slate-200 text-sm">
                    {n.items.map((it, j) => (
                        <li key={j}>{inline(it)}</li>
                    ))}
                </ul>
            );
        return (
            <p key={i} className="text-sm text-slate-200 my-1.5 leading-relaxed">
                {inline(n.text)}
            </p>
        );
    });
}

export default function AnalysisResultDialog({ open, onOpenChange, analysis, onDeleted }) {
    const [speaking, setSpeaking] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const queueRef = useRef([]);
    const keepAliveRef = useRef(null);
    const cancelledRef = useRef(false);

    useEffect(() => {
        return () => stopSpeech();
    }, []);

    useEffect(() => {
        if (!open) stopSpeech();
    }, [open]);

    const stopSpeech = () => {
        cancelledRef.current = true;
        queueRef.current = [];
        if (keepAliveRef.current) {
            clearInterval(keepAliveRef.current);
            keepAliveRef.current = null;
        }
        try {
            window.speechSynthesis?.cancel();
        } catch {
            /* noop */
        }
        setSpeaking(false);
    };

    const pickVoice = () => {
        try {
            const voices = window.speechSynthesis.getVoices() || [];
            // Prefer an English voice; fall back to whatever's available.
            return (
                voices.find((v) => /^en[-_]/i.test(v.lang)) ||
                voices.find((v) => /english/i.test(v.name)) ||
                voices[0] ||
                null
            );
        } catch {
            return null;
        }
    };

    const speakQueue = () => {
        if (cancelledRef.current) return;
        const next = queueRef.current.shift();
        if (!next) {
            if (keepAliveRef.current) {
                clearInterval(keepAliveRef.current);
                keepAliveRef.current = null;
            }
            setSpeaking(false);
            return;
        }
        const u = new SpeechSynthesisUtterance(next);
        const v = pickVoice();
        if (v) u.voice = v;
        u.lang = (v && v.lang) || "en-US";
        u.rate = 1.0;
        u.pitch = 1.0;
        u.volume = 1.0;
        u.onend = () => speakQueue();
        u.onerror = () => speakQueue();
        window.speechSynthesis.speak(u);
    };

    const speak = () => {
        if (!analysis?.summary) return;
        if (!("speechSynthesis" in window)) {
            toast.error("Your browser doesn't support speech synthesis");
            return;
        }
        if (speaking) {
            stopSpeech();
            return;
        }
        // Strip markdown so TTS reads clean sentences.
        const plain = analysis.summary
            .replace(/```[\s\S]*?```/g, "")
            .replace(/^#+\s+/gm, "")
            .replace(/\*\*/g, "")
            .replace(/`([^`]+)`/g, "$1")
            .replace(/^\s*[-*]\s+/gm, "")
            .replace(/\s+/g, " ")
            .trim();

        // Chrome (especially Android) silently drops utterances longer than
        // ~200 chars and stops after ~15s. Split into small sentence chunks
        // and queue them one-by-one.
        const chunks = plain
            .split(/(?<=[.!?])\s+/)
            .flatMap((s) => {
                if (s.length <= 180) return [s];
                const out = [];
                for (let i = 0; i < s.length; i += 180) {
                    out.push(s.slice(i, i + 180));
                }
                return out;
            })
            .filter(Boolean);

        if (chunks.length === 0) return;

        // Ensure voice list is populated before starting.
        const kickoff = () => {
            cancelledRef.current = false;
            queueRef.current = chunks;
            window.speechSynthesis.cancel();
            // Chrome-on-Android keepalive: the engine pauses itself after ~15s
            // even mid-sentence. Calling resume() on a timer keeps it going.
            if (keepAliveRef.current) clearInterval(keepAliveRef.current);
            keepAliveRef.current = setInterval(() => {
                try {
                    if (window.speechSynthesis.speaking) {
                        window.speechSynthesis.pause();
                        window.speechSynthesis.resume();
                    }
                } catch {
                    /* noop */
                }
            }, 10000);
            setSpeaking(true);
            speakQueue();
        };

        const voices = window.speechSynthesis.getVoices();
        if (voices && voices.length > 0) {
            kickoff();
        } else {
            const onVoices = () => {
                window.speechSynthesis.removeEventListener(
                    "voiceschanged",
                    onVoices,
                );
                kickoff();
            };
            window.speechSynthesis.addEventListener("voiceschanged", onVoices);
            // Some browsers never fire the event — try after a short delay too.
            setTimeout(() => {
                if (!speaking && !cancelledRef.current) kickoff();
            }, 400);
        }
    };

    const confirmDelete = async () => {
        if (!analysis?.id) return;
        setDeleting(true);
        try {
            await deleteAnalysis(analysis.id);
            onDeleted && onDeleted(analysis.id);
            toast.success("Analysis deleted");
            setPendingDelete(false);
            onOpenChange(false);
        } catch {
            toast.error("Couldn't delete");
        } finally {
            setDeleting(false);
        }
    };

    if (!analysis) return null;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[760px] max-h-[92vh] overflow-hidden p-0"
                    data-testid="analysis-result-dialog"
                >
                    <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#2A3655] bg-[#0F1B3A]/80">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Brain className="w-5 h-5 text-[#00A3FF] shrink-0" />
                            <DialogTitle className="text-white text-base">
                                {analysis.label}
                            </DialogTitle>
                            <span
                                data-testid="analysis-scope-badge"
                                className="ml-1 shrink-0 px-2 py-0.5 rounded-sm bg-[#007AFF]/20 border border-[#00A3FF]/60 text-[10px] font-bold uppercase tracking-[0.14em] text-[#00A3FF]"
                            >
                                {analysis.scope === "trade" && analysis.trade
                                    ? analysis.trade.replace(/_/g, " ")
                                    : "Overall"}
                            </span>
                        </div>
                        <DialogDescription className="text-slate-400 text-[11px] uppercase tracking-wider">
                            {analysis.style} · {analysis.document_names?.length || 0} doc(s) · {new Date(analysis.created_at).toLocaleString()}
                        </DialogDescription>
                        <div className="flex items-center gap-2 mt-3">
                            <button
                                type="button"
                                onClick={speak}
                                data-testid="btn-analysis-narrate"
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${
                                    speaking
                                        ? "bg-[#00A3FF] border-[#00A3FF] text-white"
                                        : "border-[#2A3655] text-slate-200 hover:bg-[#1C253C] hover:text-white"
                                }`}
                            >
                                {speaking ? (
                                    <>
                                        <Pause className="w-3.5 h-3.5" />
                                        Stop narration
                                    </>
                                ) : (
                                    <>
                                        <Volume2 className="w-3.5 h-3.5" />
                                        Read aloud
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setPendingDelete(true)}
                                data-testid="btn-analysis-delete"
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-semibold border border-red-500/30 text-red-300 hover:bg-red-500/10 ml-auto transition-colors"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                            </button>
                        </div>
                    </DialogHeader>
                    <div
                        className="overflow-y-auto px-5 py-4 max-h-[calc(92vh-7.5rem)]"
                        data-testid="analysis-result-body"
                    >
                        {analysis.specific_search ? (
                            <div className="text-[11px] text-slate-400 mb-3 border-l-2 border-[#00A3FF] pl-2">
                                <span className="font-bold text-slate-300">User request: </span>
                                {analysis.specific_search}
                            </div>
                        ) : null}
                        {renderMarkdown(analysis.summary)}
                        {analysis.document_names?.length > 0 && (
                            <div className="mt-5 pt-3 border-t border-[#2A3655]">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
                                    Documents analyzed
                                </div>
                                <div className="space-y-1">
                                    {analysis.document_names.map((n) => (
                                        <div
                                            key={n}
                                            className="text-xs text-slate-400 flex items-center gap-1.5"
                                        >
                                            <FileText className="w-3 h-3 text-[#00A3FF]" />
                                            {n}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={pendingDelete} onOpenChange={(v) => !deleting && setPendingDelete(v)}>
                <AlertDialogContent className="bg-[#131A2F] border border-[#2A3655] text-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete this analysis?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            "{analysis.label}" will be removed. This can't be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={deleting}
                            className="bg-transparent text-slate-300 border-[#2A3655] hover:bg-[#1C253C] hover:text-white"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            data-testid="btn-confirm-delete-analysis"
                            className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
